import { useRef, useState } from "react";
import { adminExport, adminSyncImport } from "@/lib/api";
import { Download, UploadCloud, Loader2, ArrowRightLeft } from "lucide-react";
import { toast } from "sonner";

const TARGETS = [
  { key: "content", label: "Content (kathas, mantras, stotras, puja, festivals)" },
  { key: "services", label: "Services" },
  { key: "temples", label: "Temples" },
  { key: "deities", label: "Deities" },
  { key: "hub", label: "Knowledge Hub" },
];

export default function AdminSync() {
  const [target, setTarget] = useState("content");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  const doExport = async () => {
    setBusy(true);
    try {
      const data = await adminExport(target);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `dharmartha-${target}-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast(`Exported ${data.count} ${target} item(s)`);
    } catch (err) {
      toast.error("Export failed", { description: err.response?.data?.detail || err.message });
    } finally {
      setBusy(false);
    }
  };

  const doImport = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    setResult(null);
    try {
      const res = await adminSyncImport(file, target);
      setResult(res);
      toast(`Sync complete — ${res.updated} updated, ${res.inserted} added`);
    } catch (err) {
      toast.error("Import failed", { description: err.response?.data?.detail || err.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl" data-testid="admin-sync">
      <div className="mb-2 flex items-center gap-2.5">
        <ArrowRightLeft className="h-6 w-6 text-saffron" />
        <h1 className="font-serif-latin text-3xl text-ink">Sync (Preview → Live)</h1>
      </div>
      <p className="mb-8 max-w-2xl text-sm text-ink-3">
        Curate content in your <strong>preview</strong> environment, export it here, then import the file
        in your <strong>live</strong> admin. Items are matched by slug — existing items are updated and new
        ones are added, so nothing gets duplicated and no reseed is needed.
      </p>

      <div className="border border-line bg-surface p-6">
        <label className="mb-1 block text-xs uppercase tracking-wider text-ink-3">Collection</label>
        <select
          value={target}
          onChange={(e) => setTarget(e.target.value)}
          data-testid="sync-target"
          className="w-full border border-line bg-parchment px-3 py-2 text-sm text-ink focus:border-saffron focus:outline-none"
        >
          {TARGETS.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
        </select>

        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          <div className="border border-line p-5">
            <p className="font-serif-latin text-lg text-ink">1 · Export (in Preview)</p>
            <p className="mt-1.5 text-sm text-ink-2">Download the current {target} as a JSON file.</p>
            <button
              onClick={doExport}
              disabled={busy}
              data-testid="sync-export-btn"
              className="mt-4 inline-flex items-center gap-2 bg-[var(--ink)] px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-black disabled:opacity-60"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Export JSON
            </button>
          </div>

          <div className="border border-line p-5">
            <p className="font-serif-latin text-lg text-ink">2 · Import (in Live)</p>
            <p className="mt-1.5 text-sm text-ink-2">Upload the exported JSON to upsert it here.</p>
            <button
              onClick={() => fileRef.current?.click()}
              disabled={busy}
              data-testid="sync-import-btn"
              className="mt-4 inline-flex items-center gap-2 bg-[var(--saffron)] px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-[var(--saffron-dark)] disabled:opacity-60"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />} Import JSON
            </button>
            <input ref={fileRef} type="file" accept=".json,application/json" onChange={doImport} className="hidden" data-testid="sync-file-input" />
          </div>
        </div>

        {result && (
          <div className="mt-6 border-t border-line pt-5 text-sm" data-testid="sync-result">
            <p className="text-ink">
              <span className="text-saffron">{result.updated}</span> updated ·{" "}
              <span className="text-saffron">{result.inserted}</span> added ·{" "}
              {result.total} total in file.
            </p>
            {result.errors?.length > 0 && (
              <p className="mt-2 text-ink-3">{result.errors.length} row(s) skipped (missing slug or invalid).</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
